/**
 * Admin Invites Hooks
 *
 * Custom hooks for admin invite management.
 *
 * @fileoverview React hooks for admin invite operations
 */

import { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../services/adminApi';
import {
  AdminInvite,
  InviteQueryParams,
  PaginatedResponse,
  InviteStats,
  CreateInviteInput,
  UpdateInviteInput,
  InviteCombinedStatus,
} from '../types/admin';
import { toUserMessage } from '../lib/apiError';

/**
 * Hook for managing invite list with pagination, search, and filtering
 */
export function useAdminInvites(initialParams: InviteQueryParams = {}) {
  const [invites, setInvites] = useState<AdminInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: initialParams.page || 1,
    limit: initialParams.limit || 10,
    total: 0,
    totalPages: 0,
  });
  const [params, setParams] = useState<InviteQueryParams>({
    ...initialParams,
    sortBy: initialParams.sortBy || 'createdAt',
    sortOrder: initialParams.sortOrder || 'desc',
  });

  const fetchInvites = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response: PaginatedResponse<AdminInvite> = await adminApi.invites.list(params);
      setInvites(response.data);
      setPagination({
        page: response.page,
        limit: response.limit,
        total: response.total,
        totalPages: response.totalPages,
      });
    } catch (err) {
      setError(toUserMessage(err, "We couldn't load invites. Please try again."));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchInvites();
  }, [fetchInvites]);

  const setSearch = useCallback((search: string) => {
    setParams((prev) => ({ ...prev, search, page: 1 }));
  }, []);

  const setCombinedStatus = useCallback((combinedStatus: InviteCombinedStatus | undefined) => {
    setParams((prev) => ({ ...prev, combinedStatus, page: 1 }));
  }, []);

  const setPage = useCallback((page: number) => {
    setParams((prev) => ({ ...prev, page }));
  }, []);

  const setLimit = useCallback((limit: number) => {
    setParams((prev) => ({ ...prev, limit, page: 1 }));
  }, []);

  const setSort = useCallback(
    (sortBy: InviteQueryParams['sortBy'], sortOrder: InviteQueryParams['sortOrder']) => {
      setParams((prev) => ({ ...prev, sortBy, sortOrder }));
    },
    []
  );

  const resetFilters = useCallback(() => {
    setParams({
      page: 1,
      limit: 10,
      sortBy: 'createdAt',
      sortOrder: 'desc',
    });
  }, []);

  const refetch = useCallback(() => {
    fetchInvites();
  }, [fetchInvites]);

  return {
    invites,
    loading,
    error,
    pagination,
    filters: params,
    setSearch,
    setCombinedStatus,
    setPage,
    setLimit,
    setSort,
    resetFilters,
    refetch,
  };
}

/**
 * Hook for managing a single invite
 */
export function useAdminInvite(id: string | undefined) {
  const [invite, setInvite] = useState<AdminInvite | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchInvite = useCallback(async () => {
    if (!id) {
      setInvite(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await adminApi.invites.getById(id);
      setInvite(data);
    } catch (err) {
      setError(toUserMessage(err, "We couldn't load the invite. Please try again."));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchInvite();
  }, [fetchInvite]);

  const updateInvite = useCallback(
    async (data: UpdateInviteInput) => {
      if (!id) return null;

      setLoading(true);
      setError(null);

      try {
        const updated = await adminApi.invites.update(id, data);
        setInvite(updated);
        return updated;
      } catch (err) {
        setError(toUserMessage(err, "We couldn't update the invite. Please try again."));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [id]
  );

  const deleteInvite = useCallback(async () => {
    if (!id) return false;

    setLoading(true);
    setError(null);

    try {
      await adminApi.invites.delete(id);
      setInvite(null);
      return true;
    } catch (err) {
      setError(toUserMessage(err, "We couldn't delete the invite. Please try again."));
      return false;
    } finally {
      setLoading(false);
    }
  }, [id]);

  const revokeInvite = useCallback(async () => {
    if (!id) return null;

    setLoading(true);
    setError(null);

    try {
      const updated = await adminApi.invites.update(id, { status: false });
      setInvite(updated);
      return updated;
    } catch (err) {
      setError(toUserMessage(err, "We couldn't revoke the invite. Please try again."));
      return null;
    } finally {
      setLoading(false);
    }
  }, [id]);

  const resendInvite = useCallback(async () => {
    if (!id) return null;

    setLoading(true);
    setError(null);

    try {
      const updated = await adminApi.invites.resend(id);
      setInvite(updated);
      return updated;
    } catch (err) {
      setError(toUserMessage(err, "We couldn't resend the invite. Please try again."));
      return null;
    } finally {
      setLoading(false);
    }
  }, [id]);

  return {
    invite,
    loading,
    error,
    refetch: fetchInvite,
    updateInvite,
    deleteInvite,
    revokeInvite,
    resendInvite,
  };
}

/**
 * Hook for dashboard statistics
 */
export function useAdminInviteStats() {
  const [stats, setStats] = useState<InviteStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await adminApi.invites.getStats();
      setStats(data);
    } catch (err) {
      setError(toUserMessage(err, "We couldn't load the statistics. Please try again."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    stats,
    loading,
    error,
    refetch: fetchStats,
  };
}

/**
 * Hook for creating/sending a new invite
 */
export function useSendInvite() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendInvite = useCallback(async (data: CreateInviteInput) => {
    setLoading(true);
    setError(null);

    try {
      const invite = await adminApi.invites.sendInvite(data);
      return invite;
    } catch (err) {
      setError(toUserMessage(err, "We couldn't send the invite. Please try again."));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    sendInvite,
    loading,
    error,
    clearError: () => setError(null),
  };
}

/**
 * Hook for recent invites (dashboard)
 */
export function useRecentInvites(limit: number = 5) {
  const [invites, setInvites] = useState<AdminInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRecent = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await adminApi.invites.getRecent(limit);
      setInvites(data);
    } catch (err) {
      setError(toUserMessage(err, "We couldn't load recent invites. Please try again."));
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchRecent();
  }, [fetchRecent]);

  return {
    invites,
    loading,
    error,
    refetch: fetchRecent,
  };
}

/**
 * Hook for bulk invite operations
 */
export function useInviteActions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const revokeInvite = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      const invite = await adminApi.invites.update(id, { status: false });
      return invite;
    } catch (err) {
      setError(toUserMessage(err, "We couldn't revoke the invite. Please try again."));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const resendInvite = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      const invite = await adminApi.invites.resend(id);
      return invite;
    } catch (err) {
      setError(toUserMessage(err, "We couldn't resend the invite. Please try again."));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteInvite = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      await adminApi.invites.delete(id);
      return true;
    } catch (err) {
      setError(toUserMessage(err, "We couldn't delete the invite. Please try again."));
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    revokeInvite,
    resendInvite,
    deleteInvite,
    loading,
    error,
    clearError: () => setError(null),
  };
}
